import { Link } from "react-router-dom";
import { Ear, HandHeart, Hammer, ArrowRight } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";

const JOHN_IMG =
  "https://static.prod-images.emergentagent.com/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/images/3e07929d5ad5888575da788eac7584aa55d7304a1ac8ceec482938b13564091e.jpeg";
const TOM_IMG =
  "https://customer-assets-4nw71qhi.emergentagent.net/wingman/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/attachments/4355cad9e652485cb454c37598766367_image.bin";
const PHOEBE_IMG =
  "https://customer-assets-4nw71qhi.emergentagent.net/wingman/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/attachments/f7bfd2cb3a88433a8f7bace5bd060fd2_image.bin";
const REX_IMG =
  "https://customer-assets-4nw71qhi.emergentagent.net/wingman/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/attachments/1c0ff1ee790744deb5bfeed81c48ec85_image.bin";
const COUNCIL_IMG =
  "https://customer-assets-4nw71qhi.emergentagent.net/wingman/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/attachments/81aebb0e21814ce088c2d60785817067_image.bin";

const VALUES = [
  {
    icon: Ear,
    title: "Listen First.",
    body: "Every story deserves a patient ear before it gets a platform.",
    testid: "value-listen",
  },
  {
    icon: HandHeart,
    title: "Give Freely.",
    body: "Generosity without strings is the only kind that changes things.",
    testid: "value-give",
  },
  {
    icon: Hammer,
    title: "Build Together.",
    body: "No town was ever saved by one person. We rise side by side.",
    testid: "value-build",
  },
];

const COUNCIL = [
  {
    name: "Rev. Thomas Carter",
    role: "Faith & Community",
    img: "/images/rev-thomas-carter.jpg",
    alt: "Rev. Thomas Carter, Faith and Community chair of the MyHeartGives board",
    bio: "A warm pastoral voice reminding us that service is the highest calling a neighbor can answer.",
    testid: "council-tom",
  },
  {
    name: "Phoebe Torres",
    role: "Business & Economic Development",
    img: PHOEBE_IMG,
    alt: "Phoebe Torres, Chamber President and Business and Economic Development chair",
    bio: "Chamber President and Main Street's fiercest champion — she knows strong towns are built by strong local business.",
    testid: "council-phoebe",
  },
  {
    name: "Rex Bennett",
    role: "Education & Youth",
    img: REX_IMG,
    alt: "Rex Bennett, School Superintendent and Education and Youth chair",
    bio: "School Superintendent who believes every child deserves a champion — and every school deserves a community behind it.",
    testid: "council-rex",
  },
  {
    name: "Ricco Marino",
    role: "Legacy & Entrepreneurship",
    img: "/images/ricco-marino.jpg",
    alt: "Ricco Marino, Legacy and Entrepreneurship Council",
    bio: "Builder of businesses that give back — proving legacy is measured in what you leave standing, not what you keep.",
    testid: "council-ricco",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* MISSION */}
      <section className="mx-auto max-w-7xl px-4 pb-16 pt-14 sm:px-6 lg:px-8 lg:pt-20">
        <Reveal>
          <Eyebrow>Our story</Eyebrow>
          <h1
            className="mt-4 max-w-4xl font-serif text-4xl font-bold leading-[1.15] tracking-tight text-ink sm:text-5xl lg:text-6xl"
            data-testid="about-mission"
          >
            We believe every community deserves a{" "}
            <span className="text-crimson">champion</span> and every giver
            deserves a <span className="italic text-gold">purpose</span>.
          </h1>
        </Reveal>
      </section>

      {/* JOHN */}
      <section className="bg-cream" data-testid="founder-section">
        <div className="mx-auto max-w-4xl px-4 py-20 sm:px-6 lg:px-8">
          <Reveal>
            <div
              className="mx-auto max-w-md overflow-hidden rounded-[2rem] border-4 border-gold/60 shadow-2xl"
              data-testid="founder-photo-frame"
            >
              <img
                src={JOHN_IMG}
                alt="John Delawder, founder of MyHeartGives"
                className="aspect-square w-full object-cover sepia-[0.12]"
                loading="lazy"
              />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Eyebrow className="mt-12">The founder</Eyebrow>
            <h2 className="mt-4 font-serif text-2xl font-bold tracking-tight text-ink sm:text-3xl lg:text-4xl">
              John Delawder
            </h2>
            <p className="mt-5 text-base leading-relaxed text-cocoa">
              John Delawder grew up and worked across the Midwest, from the
              hills of Ohio to the valleys of Pennsylvania, West Virginia, and
              Kentucky — places where neighbors show up before they're asked.
              Years later, he packed his life into a van and traveled America,
              meeting people in booming cities and forgotten towns. Everywhere
              he went, he found the same truth: good people carrying worthy
              causes, with no one to hear them.
            </p>
            <p className="mt-4 font-serif text-lg italic leading-relaxed text-ink">
              MyHeartGives is his answer — a movement built on the open road,
              created with heart and authenticity, for communities and people
              who still believe in giving back.
            </p>
          </Reveal>
        </div>
      </section>

      {/* COUNCIL / BOARD */}
      <section className="bg-cream" data-testid="council-section">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Reveal>
            <Eyebrow>The council</Eyebrow>
            <h2 className="mt-4 font-serif text-2xl font-bold tracking-tight text-ink sm:text-3xl lg:text-4xl">
              Our Board of Directors
            </h2>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-cocoa">
              A council of neighbors — faith, business, education, and
              enterprise — keeping MyHeartGives rooted in the communities it
              serves.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <figure
              className="mt-10 overflow-hidden rounded-[2rem] border-4 border-gold/60 shadow-2xl"
              data-testid="council-group-photo"
            >
              <img
                src={COUNCIL_IMG}
                alt="Ricco Marino and the full MyHeartGives Board of Directors council, gathered together"
                className="aspect-[16/8] w-full object-cover object-top sepia-[0.12]"
                loading="lazy"
              />
              <figcaption className="bg-white px-6 py-4 text-sm italic text-cocoa">
                Ricco Marino and the MyHeartGives Board of Directors
              </figcaption>
            </figure>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {COUNCIL.map((m, i) => (
              <Reveal key={m.testid} delay={0.08 * i}>
                <div
                  className="group h-full overflow-hidden rounded-3xl border border-line bg-white transition-all duration-200 hover:-translate-y-1.5 hover:border-gold/60 hover:shadow-lg"
                  data-testid={m.testid}
                >
                  <img
                    src={m.img}
                    alt={m.alt}
                    className="aspect-square w-full select-none object-cover object-[50%_20%] sepia-[0.12]"
                    draggable="false"
                    loading="lazy"
                  />
                  <div className="p-6">
                    <h3 className="font-serif text-lg font-semibold text-ink">
                      {m.name}
                    </h3>
                    <p className="mt-1 font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-gold">
                      {m.role}
                    </p>
                    <p className="mt-3 text-sm leading-relaxed text-cocoa">
                      {m.bio}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-ink" data-testid="values-section">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Reveal>
            <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-gold-bright">
              What we live by
            </p>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-10 md:grid-cols-3">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={0.1 * i}>
                <div data-testid={v.testid}>
                  <v.icon className="h-7 w-7 text-gold" />
                  <h3 className="mt-4 font-serif text-2xl font-bold italic text-ivory sm:text-3xl">
                    {v.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-ivory/70 sm:text-base">
                    {v.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-16 flex flex-wrap gap-4">
              <Link
                to="/causes"
                className="inline-flex items-center gap-2 rounded-full bg-crimson px-7 py-3.5 text-sm font-semibold text-ivory transition-colors duration-200 hover:bg-crimson-deep"
                data-testid="about-cta-cause"
              >
                For Causes <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/give"
                className="inline-flex items-center gap-2 rounded-full border-2 border-gold px-7 py-3 text-sm font-semibold text-ivory transition-colors duration-200 hover:bg-gold hover:text-ink"
                data-testid="about-cta-give"
              >
                For Givers
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
